import React, {useMemo} from 'react'
import {useTestContext} from './TestContext'
import Button from 'app/components/Button'
import TestStatusBadge from './TestStatusBadge'
import {FlagEvidenceModal, MarkAsNotRequiredModal} from './modals'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {getReviewStatus} from '../../utils/reviewStatusUtils'
import FormatTimestamp from 'app/components/FormatTimestamp'

const TestOverview: React.FC = () => {
  const {
    selectedTest,
    activeFrameworks,
    handleTimeframe,
    handleOwner,
    personnels,
    currentUser,
    handleOpenFlagModal,
    handleAccept,
    handleReEnableEvidence,
    handleOpenMarkAsNotRequiredModal,
    currentAuditContext,
    audits,
    handleOpenMarkAsResolvedModal,
    isMarkAsResolvedModalOpen,
    handleCloseMarkAsResolvedModal,
    handleConfirmMarkAsResolved,
    isMarkingAsResolved,
    isRefreshingTest,
    handleRefreshTest,
  } = useTestContext()

  const shouldShowAuditorReview = useMemo(() => {
    return currentUser?.role === 'Auditor' || currentAuditContext
  }, [currentUser?.role, currentAuditContext])

  if (selectedTest.length === 0) {
    return <div>Loading...</div>
  }
  const test = selectedTest[0]
  const currentAuditId = audits && audits.length > 0 ? audits[0].id : null

  return (
    <div>
      <div
        className='d-flex flex-column scroll-y pe-7'
        id='kt_modal_add_user_scroll'
        data-kt-scroll='true'
        data-kt-scroll-activate='{default: false, lg: true}'
        data-kt-scroll-max-height='auto'
        data-kt-scroll-dependencies='#kt_modal_add_user_header'
        data-kt-scroll-wrappers='#kt_modal_add_user_scroll'
        data-kt-scroll-offset='300px'
      >
        <div className='fv-row mb-4 text-gray-800 fs-6'>
          <div
            className='fv-row mb-4 text-gray-800 fs-6'
            dangerouslySetInnerHTML={{__html: test.description}}
          />
        </div>
        {shouldShowAuditorReview ? (
          <div className='row mb-5'>
            <div className='col-3'>
              <label className='text-gray-800 fw-semibold me-2 fs-6'>Auditor Review</label>
            </div>
            <div className='col-9'>
              <span className='fw-normal fs-6'>
                <TestStatusBadge
                  status={test.status}
                  auditorReview={test.auditor_review}
                  isAuditor={true}
                  auditId={currentAuditId}
                />
              </span>
            </div>
          </div>
        ) : (
          <div className='row mb-5'>
            <div className='col-3'>
              <label className='text-gray-800 fw-semibold me-2 fs-6'>Status</label>
            </div>
            <div className='col-9'>
              <span className='fw-normal fs-6'>
                <TestStatusBadge status={test.status} isAuditor={false} />
              </span>
            </div>
          </div>
        )}
        <div className='row mb-5'>
          <div className='col-3'>
            <label className='text-gray-800 fw-semibold me-2 fs-6 '>Category</label>
          </div>
          <div className='col-9'>
            <span className='text-normal fs-6'>{test.category}</span>
          </div>
        </div>
        <div className='row mb-5'>
          <div className='col-3'>
            <label className='text-gray-800 fw-semibold me-2 fs-6 '>Type</label>
          </div>
          <div className='col-9'>
            <span className='fw-normal fs-6'>
              {test.type?.charAt(0).toUpperCase() + test.type?.slice(1)}
            </span>
          </div>
        </div>
        {test.automated_type && (
          <div className='row mb-5'>
            <div className='col-3'>
              <label className='text-gray-800 fw-semibold me-2 fs-6 '>Integration</label>
            </div>
            <div className='col-9'>
              <span className='fw-normal fs-6'>{test.integration}</span>
            </div>
          </div>
        )}
        {test.automated_type && (
          <div className='row mb-5'>
            <div className='col-3'>
              <label className='text-gray-800 fw-semibold me-2 fs-6 '>Updated</label>
            </div>
            <div className='col-9'>
               <span className='fw-normal fs-6 d-flex align-items-center gap-2'>
                 {FormatTimestamp({timestamp: test.updated_at})}{' '}
                 <span 
                   style={{cursor: isRefreshingTest ? 'not-allowed' : 'pointer'}} 
                   onClick={isRefreshingTest ? undefined : handleRefreshTest}
                 >
                   {isRefreshingTest ? (
                     <div className='spinner-border spinner-border-sm' role='status'>
                       <span className='visually-hidden'>Loading...</span>
                     </div>
                   ) : (
                     <svg
                       xmlns='http://www.w3.org/2000/svg'
                       width='18'
                       height='18'
                       viewBox='0 0 24 24'
                       fill='none'
                       stroke='currentColor'
                       strokeWidth='2'
                       strokeLinecap='round'
                       strokeLinejoin='round'
                       className='icon icon-tabler icons-tabler-outline icon-tabler-refresh'
                     >
                       <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                       <path d='M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4' />
                       <path d='M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4' />
                     </svg>
                   )}
                 </span>
               </span>
            </div>
          </div>
        )}
        <div className='row mb-3'>
          <div className='col-3'>
            <label className='text-gray-800 fw-semibold me-2 fs-6 '>Framework</label>
          </div>
          <div className='col-9'>
            {test.framework
              .split(', ')
              .filter((framework) => activeFrameworks.some((item) => framework.startsWith(item)))
              .map((framework, index) => (
                <span
                  key={index}
                  className='badge px-2 py-1 rounded-pill mb-1 fs-7 me-1'
                  style={{border: '2px solid #E1E1E1'}}
                >
                  {framework}
                </span>
              ))}
          </div>
        </div>

        <div className='row mb-3'>
          <div className='col-3 d-flex align-items-center'>
            <label className='text-gray-800 fw-semibold me-2 fs-6 '>Test interval</label>
          </div>
          <div className='col-9'>
            <span className='fw-normal'>
              {' '}
              <select
                name='timeframe'
                className='form-select py-2 px-4'
                data-control='select2'
                data-placeholder='Select time interval'
                value={test.timeframe || ''}
                onChange={handleTimeframe}
                disabled={currentUser?.role === 'Auditor' || test.status === 'Deactivated'}
              >
                <option value='' selected>
                  Select time interval
                </option>
                <option value='Annually'>Annually (Recommended)</option>
                <option value='Bi-annually'>Bi-annually</option>
                <option value='Quarterly'>Quarterly</option>
                <option value='Monthly'>Monthly</option>
                <option value='Weekly'>Weekly</option>
                <option value='Never'>Never</option>
              </select>
            </span>
          </div>
        </div>

        <div className='row mb-3'>
          <div className='col-3 d-flex align-items-center'>
            <label className='text-gray-800 fw-semibold me-2 fs-6 '>Owner</label>
          </div>
          <div className='col-9'>
            <span className='fw-normal'>
              {' '}
              <select
                name='owner'
                className='form-select py-2 px-4'
                data-control='select2'
                data-placeholder='Select Owner'
                value={test.owner || ''}
                onChange={handleOwner}
                disabled={currentUser?.role === 'Auditor' || test.status === 'Deactivated'}
              >
                <option value=''>Assign Owner</option>
                {personnels
                  .filter((user) => user.role === 'Admin' || user.role === 'Owner')
                  .map((group) => (
                    <option key={group.id} value={group.full_name}>
                      {group.full_name}
                    </option>
                  ))}
              </select>
            </span>
          </div>
        </div>

        {currentUser?.role === 'Auditor' && (
          <div className='row'>
            <div className='col-3 d-flex align-items-center'>
              <label className='text-gray-800 fw-semibold me-2 fs-6'>Actions</label>
            </div>
            <div className='col-9 d-flex gap-2 flex-wrap'>
              {(() => {
                const auditorReview = test.auditor_review
                const status = test.status
                const currentAuditId = audits && audits.length > 0 ? audits[0].id : null
                const reviewStatus = getReviewStatus(auditorReview, currentAuditId)

                if (reviewStatus === 'Not applicable') {
                  return (
                    <Button
                      btnType='default'
                      onClick={handleReEnableEvidence}
                      style={{whiteSpace: 'nowrap', minWidth: 'fit-content'}}
                    >
                      Re-enable evidence
                    </Button>
                  )
                }

                return (
                  <>
                    <Button
                      btnType='default'
                      className='text-primary'
                      onClick={handleAccept}
                      disabled={
                        status === 'Fail' || status === 'Deactivated' || reviewStatus === 'Approved'
                      }
                      style={{whiteSpace: 'nowrap', minWidth: 'fit-content'}}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-check me-2'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M5 12l5 5l10 -10' />
                      </svg>
                      Accept
                    </Button>
                    <Button
                      btnType='default'
                      className='text-danger'
                      onClick={handleOpenFlagModal}
                      disabled={reviewStatus === 'Flagged' || status === 'Fail'}
                      style={{whiteSpace: 'nowrap', minWidth: 'fit-content'}}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-flag-3 me-2'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M5 14h14l-4.5 -4.5l4.5 -4.5h-14v16' />
                      </svg>
                      Flag
                    </Button>
                    <Button
                      btnType='default'
                      onClick={handleOpenMarkAsNotRequiredModal}
                      style={{whiteSpace: 'nowrap', minWidth: 'fit-content'}}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-ban me-2'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                        <path d='M5.7 5.7l12.6 12.6' />
                      </svg>
                      <span className='d-none d-md-inline'>Mark as not required</span>
                      <span className='d-md-none'>Not required</span>
                    </Button>
                  </>
                )
              })()}
            </div>
          </div>
        )}

        {currentAuditContext &&
          currentUser?.role !== 'Auditor' &&
          (() => {
            const auditorReview = test.auditor_review
            const currentAuditId = audits && audits.length > 0 ? audits[0].id : null
            const reviewStatus = getReviewStatus(auditorReview, currentAuditId)

            if (reviewStatus === 'Flagged') {
              return (
                <div className='row'>
                  <div className='col-3 d-flex align-items-center'>
                    <label className='text-gray-800 fw-semibold me-2 fs-6'>Actions</label>
                  </div>
                  <div className='col-9 d-flex gap-2 flex-wrap'>
                    <Button
                      btnType='default'
                      className='text-success'
                      onClick={handleOpenMarkAsResolvedModal}
                      style={{whiteSpace: 'nowrap', minWidth: 'fit-content'}}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        stroke-width='2'
                        stroke-linecap='round'
                        stroke-linejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-check me-2'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M5 12l5 5l10 -10' />
                      </svg>
                      Mark as Resolved
                    </Button>
                  </div>
                </div>
              )
            }
            return null
          })()}
      </div>

      <FlagEvidenceModal />
      <MarkAsNotRequiredModal />
      <ConfirmationModal
        isModalOpen={isMarkAsResolvedModalOpen}
        hideModal={handleCloseMarkAsResolvedModal}
        confirmBtnClickHandler={handleConfirmMarkAsResolved}
        title='Mark as Resolved'
        message="Are you sure you want to mark this flagged evidence as resolved? This will change the status to 'Ready for Audit'."
        confirmButtonText='Mark as Resolved'
        type='default'
        isLoading={isMarkingAsResolved}
      />
    </div>
  )
}

export default TestOverview
